import { useState } from 'react';
import { formatCurrency } from '@/lib/format';
import FlyerImovelModal from '@/components/feature/FlyerImovelModal';

export interface CompartilharImovelData {
  id: string;
  codigo: string;
  titulo: string;
  bairro: string;
  cidade: string;
  preco: number;
  tipo: string;
  quartos?: number;
  area_total?: number;
  slug: string;
  foto_capa_url?: string;
  fotos?: { url: string }[];
}

interface CompartilharImovelModalProps {
  imovel: CompartilharImovelData;
  isOpen: boolean;
  onClose: () => void;
}

const BASE_URL = window.location.origin;

// URL da Edge Function que serve as meta tags OG corretas para bots do WhatsApp/Facebook
const OG_PROXY_URL = 'https://qyzqwoeqmepyvypmxddg.supabase.co/functions/v1/og-meta-proxy';

export default function CompartilharImovelModal({ imovel, isOpen, onClose }: CompartilharImovelModalProps) {
  const [copiado, setCopiado] = useState(false);
  const [mensagemPersonalizada, setMensagemPersonalizada] = useState('');
  const [showFlyer, setShowFlyer] = useState(false);

  if (!isOpen) return null;

  const imagemCapa = imovel.foto_capa_url || imovel.fotos?.[0]?.url || '';

  // Link real do imóvel (mostrado ao usuário e copiado)
  const linkImovel = `${BASE_URL}/imovel/${imovel.slug}`;

  // Link especial para WhatsApp — aponta para a Edge Function que tem og:image correto,
  // e redireciona automaticamente para a página real após o bot ler as meta tags
  const linkWhatsApp = `${OG_PROXY_URL}?slug=${imovel.slug}`;

  const mensagemPadrao = `🏠 *${imovel.titulo}*

📍 ${imovel.bairro}, ${imovel.cidade}
🏷️ ${imovel.tipo}${imovel.quartos ? ` · ${imovel.quartos} quartos` : ''}${imovel.area_total ? ` · ${imovel.area_total}m²` : ''}
💰 *${formatCurrency(imovel.preco)}*

🔗 Veja mais detalhes e fotos:
${linkWhatsApp}

_Mateus Oliveira Imóveis · (79) 99988-1002_`;

  // Na mensagem personalizada, usa o link da Edge Function para garantir miniatura
  const mensagemFinal = mensagemPersonalizada.trim()
    ? `${mensagemPersonalizada.trim()}\n\n${linkWhatsApp}`
    : mensagemPadrao;

  function handleCopiarLink() {
    navigator.clipboard.writeText(linkImovel).then(() => {
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    });
  }

  function handleWhatsApp() {
    window.open(`https://wa.me/?text=${encodeURIComponent(mensagemFinal)}`, '_blank');
  }

  function handleWhatsAppDireto(numero: string) {
    window.open(`https://wa.me/55${numero.replace(/\D/g, '')}?text=${encodeURIComponent(mensagemFinal)}`, '_blank');
  }

  function handleFacebook() {
    // Facebook também usa o link da Edge Function para capturar og:image corretamente
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(linkWhatsApp)}`, '_blank');
  }

  function handleNativeShare() {
    if (navigator.share) {
      navigator.share({
        title: imovel.titulo,
        text: `${imovel.tipo} em ${imovel.bairro}, ${imovel.cidade} — ${formatCurrency(imovel.preco)}`,
        url: linkImovel,
      });
    }
  }

  const hasNativeShare = typeof navigator !== 'undefined' && !!navigator.share;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-white/20 rounded-lg">
              <i className="ri-share-forward-line text-white text-xl"></i>
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Compartilhar Imóvel</h2>
              <p className="text-green-100 text-xs">{imovel.codigo}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-all cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="p-6 space-y-5 max-h-[80vh] overflow-y-auto">
          {/* Preview — como aparece no WhatsApp */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
              <i className="ri-eye-line"></i>
              Preview — como aparece no WhatsApp
            </p>
            <div className="border border-gray-200 rounded-xl overflow-hidden bg-gray-50">
              <div className="flex">
                {imagemCapa ? (
                  <div className="w-28 h-24 flex-shrink-0">
                    <img
                      src={imagemCapa}
                      alt={imovel.titulo}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="w-28 h-24 flex-shrink-0 bg-gray-200 flex items-center justify-center">
                    <i className="ri-image-line text-2xl text-gray-400"></i>
                  </div>
                )}
                <div className="p-3 flex-1 min-w-0">
                  <p className="text-xs text-green-600 font-semibold truncate">{BASE_URL}</p>
                  <p className="text-sm font-bold text-gray-900 line-clamp-2 leading-tight mt-0.5">
                    {imovel.titulo}
                  </p>
                  <p className="text-xs text-gray-500 mt-1 truncate">
                    {imovel.bairro}, {imovel.cidade} · {formatCurrency(imovel.preco)}
                  </p>
                </div>
              </div>
            </div>
            {imagemCapa ? (
              <p className="text-xs text-green-600 mt-1.5 flex items-center gap-1">
                <i className="ri-checkbox-circle-line"></i>
                Miniatura configurada — a foto vai aparecer no WhatsApp
              </p>
            ) : (
              <p className="text-xs text-amber-600 mt-1.5 flex items-center gap-1">
                <i className="ri-information-line"></i>
                Sem foto capa definida — a miniatura pode não aparecer no WhatsApp
              </p>
            )}
          </div>

          {/* Link */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Link do Imóvel</p>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-700 truncate font-mono">
                {linkImovel}
              </div>
              <button
                onClick={handleCopiarLink}
                className={`flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  copiado
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <i className={copiado ? 'ri-check-line' : 'ri-file-copy-line'}></i>
                {copiado ? 'Copiado!' : 'Copiar'}
              </button>
            </div>
          </div>

          {/* Mensagem personalizada */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Mensagem personalizada{' '}
              <span className="font-normal normal-case text-gray-400">(opcional)</span>
            </p>
            <textarea
              value={mensagemPersonalizada}
              onChange={(e) => setMensagemPersonalizada(e.target.value)}
              rows={2}
              placeholder="Ex: Olá! Separei este imóvel especialmente para você..."
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
              maxLength={200}
            />
            <p className="text-xs text-gray-400 text-right mt-1">{mensagemPersonalizada.length}/200</p>
          </div>

          {/* Botões */}
          <div className="space-y-3">
            {/* Flyer Digital */}
            <button
              onClick={() => setShowFlyer(true)}
              className="w-full flex items-center justify-center gap-3 bg-orange-600 hover:bg-orange-700 text-white font-bold py-3.5 rounded-xl transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-image-2-line text-2xl"></i>
              Gerar Flyer Digital
            </button>

            {/* WhatsApp */}
            <button
              onClick={handleWhatsApp}
              className="w-full flex items-center justify-center gap-3 bg-green-500 hover:bg-green-600 text-white font-bold py-3.5 rounded-xl transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-2xl"></i>
              Compartilhar no WhatsApp
            </button>

            {/* Outras opções */}
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={handleFacebook}
                className="flex items-center justify-center gap-2 bg-[#1877F2] hover:bg-[#166FE5] text-white font-semibold py-2.5 rounded-xl transition-all whitespace-nowrap cursor-pointer text-sm"
              >
                <i className="ri-facebook-fill text-lg"></i>
                Facebook
              </button>
              {hasNativeShare ? (
                <button
                  onClick={handleNativeShare}
                  className="flex items-center justify-center gap-2 bg-gray-700 hover:bg-gray-800 text-white font-semibold py-2.5 rounded-xl transition-all whitespace-nowrap cursor-pointer text-sm"
                >
                  <i className="ri-share-line text-lg"></i>
                  Mais opções
                </button>
              ) : (
                <button
                  onClick={handleCopiarLink}
                  className={`flex items-center justify-center gap-2 font-semibold py-2.5 rounded-xl transition-all whitespace-nowrap cursor-pointer text-sm ${
                    copiado ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <i className={copiado ? 'ri-check-line text-lg' : 'ri-link text-lg'}></i>
                  {copiado ? 'Copiado!' : 'Copiar link'}
                </button>
              )}
            </div>

            {/* Divisor */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-gray-200"></div>
              <span className="text-xs text-gray-400">ou enviar direto para</span>
              <div className="flex-1 h-px bg-gray-200"></div>
            </div>

            {/* Enviar para número */}
            <EnviarParaNumero onEnviar={handleWhatsAppDireto} />
          </div>
        </div>
      </div>

      {/* Modal do Flyer (z-index acima do modal atual) */}
      <FlyerImovelModal
        imovel={imovel}
        isOpen={showFlyer}
        onClose={() => setShowFlyer(false)}
      />
    </div>
  );
}

// ── Sub-componente ─────────────────────────────────────────────────────────────
function EnviarParaNumero({ onEnviar }: { onEnviar: (numero: string) => void }) {
  const [numero, setNumero] = useState('');

  function formatarNumero(valor: string) {
    const digits = valor.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 2) return digits;
    if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }

  return (
    <div className="flex items-center gap-2">
      <input
        type="tel"
        value={numero}
        onChange={(e) => setNumero(formatarNumero(e.target.value))}
        placeholder="(79) 99999-9999"
        className="flex-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
      />
      <button
        onClick={() => {
          if (numero.replace(/\D/g, '').length >= 10) onEnviar(numero);
        }}
        disabled={numero.replace(/\D/g, '').length < 10}
        className="flex items-center gap-1.5 px-4 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
      >
        <i className="ri-send-plane-line"></i>
        Enviar
      </button>
    </div>
  );
}
